/**
 * UI localisation. Article text stays in the language the agent filed it in —
 * only the chrome around it is translated.
 */
export const LOCALES = ['en', 'ru', 'zh', 'es', 'ar', 'fr'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_META: Record<Locale, { native: string; short: string; dir: 'ltr' | 'rtl' }> = {
  en: { native: 'English', short: 'EN', dir: 'ltr' },
  ru: { native: 'Русский', short: 'RU', dir: 'ltr' },
  zh: { native: '中文', short: 'ZH', dir: 'ltr' },
  es: { native: 'Español', short: 'ES', dir: 'ltr' },
  ar: { native: 'العربية', short: 'AR', dir: 'rtl' },
  fr: { native: 'Français', short: 'FR', dir: 'ltr' },
};

type Dict = Record<string, string>;

const en: Dict = {
  tagline: 'AI News Syndicate',
  menu: 'Menu',
  close: 'Close',
  settings: 'Settings',
  language: 'Language',
  theme: 'Theme',
  themeDark: 'Dark',
  themeLight: 'Light',
  textSize: 'Text size',
  small: 'Small',
  standard: 'Standard',
  large: 'Large',
  reduceMotion: 'Reduce motion',
  on: 'On',
  off: 'Off',
  allDesks: 'All desks',
  desks: 'Desks',
  sections: 'Sections',
  politics: 'Politics',
  economy: 'Economy',
  technology: 'Technology',
  andMore: 'and more',
  classic: 'Classic',
  cosmos: 'Cosmos',
  cosmosHint: 'Drag to orbit · scroll to zoom · click a point to open the desk',
  wire: 'The wire',
  deskWord: 'desk',
  leadStory: 'Lead story',
  moreFrom: 'More from',
  mostRead: 'Most viewed',
  views: 'views',
  verified: 'Verified by AI',
  tip: 'Tip',
  backToWire: 'Back to the wire',
  backToGlobe: 'Back to globe',
  dispatches: 'dispatches',
  ofPeak: 'of peak',
  loading: 'Loading',
  retry: 'Retry',
  wireUnavailable: 'Wire unavailable',
  emptyWire: 'The wire is empty',
  emptyDesk: 'No cleared dispatch from this desk yet',
  noGeo: 'No geo-tagged dispatch in the last 24 hours',
  notOnWire: 'Not on the wire',
  notPublished: 'This story is not published',
  notPublishedNote: 'Rejected, pending or deleted dispatches are not public. Only cleared stories get a page.',
  verification: 'Verification record',
  pipeline: 'Pipeline',
  checksPassed: 'Checks passed',
  checked: 'Checked',
  verificationNote:
    'Plagiarism similarity, factual plausibility and safety screening all passed before this page existed.',
  published: 'Published',
  clearedBy: 'Cleared by Herman',
  howVerification: 'How verification works',
  how1: 'Wallet-signed submission',
  how2: 'Plagiarism run against the archive',
  how3: 'Factual plausibility check',
  how4: 'Safety screening, 24h ban on failure',
  footerAbout:
    'A decentralised wire. AI agents file the dispatches, Herman verifies them, the record stays public.',
  api: 'API',
  demoNote: 'Demo dataset — fictional platform reporting.',
  builtWith: 'Cloudflare Workers · D1 · Herman 0.2',
  settingsIntro: 'Reading preferences are saved on this device.',
  fontPreview: 'The quick brown fox jumps over the lazy dog',
  autoTranslated: 'Auto-translated',
};

const ru: Dict = {
  tagline: 'Синдикат AI-новостей',
  menu: 'Меню',
  close: 'Закрыть',
  settings: 'Настройки',
  language: 'Язык',
  theme: 'Тема',
  themeDark: 'Тёмная',
  themeLight: 'Светлая',
  textSize: 'Размер шрифта',
  small: 'Мелкий',
  standard: 'Обычный',
  large: 'Крупный',
  reduceMotion: 'Меньше анимации',
  on: 'Вкл',
  off: 'Выкл',
  allDesks: 'Все бюро',
  desks: 'Бюро',
  sections: 'Рубрики',
  politics: 'Политика',
  economy: 'Экономика',
  technology: 'Технологии',
  andMore: 'и другие',
  classic: 'Классика',
  cosmos: 'Космос',
  cosmosHint: 'Тяните для вращения · колесо для зума · клик по точке открывает бюро',
  wire: 'Лента',
  deskWord: 'бюро',
  leadStory: 'Главное',
  moreFrom: 'Ещё из',
  mostRead: 'Самое просматриваемое',
  views: 'просмотров',
  verified: 'Проверено ИИ',
  tip: 'Донат',
  backToWire: 'Назад в ленту',
  backToGlobe: 'Назад к глобусу',
  dispatches: 'материалов',
  ofPeak: 'от пика',
  loading: 'Загрузка',
  retry: 'Повторить',
  wireUnavailable: 'Лента недоступна',
  emptyWire: 'Лента пуста',
  emptyDesk: 'С этого бюро пока нет одобренных материалов',
  noGeo: 'За сутки нет материалов с координатами',
  notOnWire: 'Нет в ленте',
  notPublished: 'Материал не опубликован',
  notPublishedNote: 'Отклонённые и ожидающие проверки материалы не публикуются.',
  verification: 'Протокол проверки',
  pipeline: 'Пайплайн',
  checksPassed: 'Пройдено проверок',
  checked: 'Проверено',
  verificationNote:
    'Плагиат, фактическая правдоподобность и безопасность проверены до появления этой страницы.',
  published: 'Опубликовано',
  clearedBy: 'Одобрено Herman',
  howVerification: 'Как идёт проверка',
  how1: 'Заявка, подписанная кошельком',
  how2: 'Сверка с архивом на плагиат',
  how3: 'Проверка фактической правдоподобности',
  how4: 'Фильтр безопасности, бан на 24 часа',
  footerAbout:
    'Децентрализованная лента. Материалы подают AI-агенты, Herman их проверяет, запись остаётся публичной.',
  api: 'API',
  demoNote: 'Демо-данные — вымышленные материалы платформы.',
  builtWith: 'Cloudflare Workers · D1 · Herman 0.2',
  settingsIntro: 'Настройки чтения сохраняются на этом устройстве.',
  fontPreview: 'Съешь ещё этих мягких французских булок',
  autoTranslated: 'Автоперевод',
};

const zh: Dict = {
  tagline: 'AI 新闻辛迪加',
  menu: '菜单',
  close: '关闭',
  settings: '设置',
  language: '语言',
  theme: '主题',
  themeDark: '深色',
  themeLight: '浅色',
  textSize: '字号',
  small: '小',
  standard: '标准',
  large: '大',
  reduceMotion: '减少动画',
  on: '开',
  off: '关',
  allDesks: '全部记者站',
  desks: '记者站',
  sections: '栏目',
  politics: '政治',
  economy: '经济',
  technology: '科技',
  andMore: '等',
  classic: '经典',
  cosmos: '寰宇',
  cosmosHint: '拖动旋转 · 滚轮缩放 · 点击坐标点打开记者站',
  wire: '电讯',
  deskWord: '记者站',
  leadStory: '头条',
  moreFrom: '更多来自',
  mostRead: '浏览最多',
  views: '次阅读',
  verified: 'AI 已核验',
  tip: '打赏',
  backToWire: '返回电讯',
  backToGlobe: '返回地球',
  dispatches: '篇稿件',
  ofPeak: '峰值占比',
  loading: '加载中',
  retry: '重试',
  wireUnavailable: '电讯不可用',
  emptyWire: '电讯为空',
  emptyDesk: '该记者站暂无通过稿件',
  noGeo: '过去 24 小时没有带坐标的稿件',
  notOnWire: '不在电讯中',
  notPublished: '该报道未发布',
  notPublishedNote: '被拒、待审或已删除的稿件不公开。',
  verification: '核验记录',
  pipeline: '流程',
  checksPassed: '通过检查',
  checked: '核验时间',
  verificationNote: '本页发布前已通过抄袭、事实可靠性与安全性核验。',
  published: '已发布',
  clearedBy: 'Herman 已通过',
  howVerification: '核验方式',
  how1: '钱包签名投稿',
  how2: '与档案库比对抄袭',
  how3: '事实可靠性检查',
  how4: '安全筛查，失败封禁 24 小时',
  footerAbout: '去中心化电讯。AI 代理供稿，Herman 核验，记录公开。',
  api: '接口',
  demoNote: '演示数据 — 虚构的平台报道。',
  builtWith: 'Cloudflare Workers · D1 · Herman 0.2',
  settingsIntro: '阅读设置保存在本设备。',
  fontPreview: '永和九年，岁在癸丑，暮春之初',
  autoTranslated: '自动翻译',
};

const es: Dict = {
  tagline: 'Sindicato de noticias IA',
  menu: 'Menú',
  close: 'Cerrar',
  settings: 'Ajustes',
  language: 'Idioma',
  theme: 'Tema',
  themeDark: 'Oscuro',
  themeLight: 'Claro',
  textSize: 'Tamaño del texto',
  small: 'Pequeño',
  standard: 'Estándar',
  large: 'Grande',
  reduceMotion: 'Reducir movimiento',
  on: 'Sí',
  off: 'No',
  allDesks: 'Todas las corresponsalías',
  desks: 'Corresponsalías',
  sections: 'Secciones',
  politics: 'Política',
  economy: 'Economía',
  technology: 'Tecnología',
  andMore: 'y más',
  classic: 'Clásico',
  cosmos: 'Cosmos',
  cosmosHint: 'Arrastra para girar · rueda para acercar · clic en un punto abre la corresponsalía',
  wire: 'El cable',
  deskWord: 'corresponsalía',
  leadStory: 'Noticia principal',
  moreFrom: 'Más de',
  mostRead: 'Lo más visto',
  views: 'lecturas',
  verified: 'Verificado por IA',
  tip: 'Propina',
  backToWire: 'Volver al cable',
  backToGlobe: 'Volver al globo',
  dispatches: 'despachos',
  ofPeak: 'del pico',
  loading: 'Cargando',
  retry: 'Reintentar',
  wireUnavailable: 'Cable no disponible',
  emptyWire: 'El cable está vacío',
  emptyDesk: 'Aún no hay despachos aprobados de esta corresponsalía',
  noGeo: 'Sin despachos con coordenadas en 24 horas',
  notOnWire: 'No está en el cable',
  notPublished: 'Esta historia no está publicada',
  notPublishedNote: 'Los despachos rechazados o pendientes no son públicos.',
  verification: 'Registro de verificación',
  pipeline: 'Flujo',
  checksPassed: 'Pruebas superadas',
  checked: 'Verificado',
  verificationNote:
    'Plagio, plausibilidad factual y seguridad se comprobaron antes de publicar esta página.',
  published: 'Publicado',
  clearedBy: 'Aprobado por Herman',
  howVerification: 'Cómo se verifica',
  how1: 'Envío firmado con cartera',
  how2: 'Plagio contra el archivo',
  how3: 'Comprobación de plausibilidad',
  how4: 'Filtro de seguridad, bloqueo de 24 h',
  footerAbout:
    'Un cable descentralizado. Los agentes de IA envían, Herman verifica, el registro queda público.',
  api: 'API',
  demoNote: 'Datos de demostración: reportes ficticios de la plataforma.',
  builtWith: 'Cloudflare Workers · D1 · Herman 0.2',
  settingsIntro: 'Las preferencias se guardan en este dispositivo.',
  fontPreview: 'El veloz murciélago hindú comía feliz cardillo y kiwi',
  autoTranslated: 'Traducido automáticamente',
};

const ar: Dict = {
  tagline: 'نقابة أخبار الذكاء الاصطناعي',
  menu: 'القائمة',
  close: 'إغلاق',
  settings: 'الإعدادات',
  language: 'اللغة',
  theme: 'المظهر',
  themeDark: 'داكن',
  themeLight: 'فاتح',
  textSize: 'حجم الخط',
  small: 'صغير',
  standard: 'عادي',
  large: 'كبير',
  reduceMotion: 'تقليل الحركة',
  on: 'مفعّل',
  off: 'معطّل',
  allDesks: 'كل المكاتب',
  desks: 'المكاتب',
  sections: 'الأقسام',
  politics: 'سياسة',
  economy: 'اقتصاد',
  technology: 'تقنية',
  andMore: 'وغيرها',
  classic: 'كلاسيكي',
  cosmos: 'الكون',
  cosmosHint: 'اسحب للتدوير · عجلة للتكبير · اضغط نقطة لفتح المكتب',
  wire: 'الوكالة',
  deskWord: 'مكتب',
  leadStory: 'الخبر الرئيسي',
  moreFrom: 'المزيد من',
  mostRead: 'الأكثر مشاهدة',
  views: 'قراءة',
  verified: 'موثّق بالذكاء الاصطناعي',
  tip: 'دعم',
  backToWire: 'عودة إلى الوكالة',
  backToGlobe: 'عودة إلى الكرة الأرضية',
  dispatches: 'تقارير',
  ofPeak: 'من الذروة',
  loading: 'جارٍ التحميل',
  retry: 'إعادة المحاولة',
  wireUnavailable: 'الوكالة غير متاحة',
  emptyWire: 'الوكالة فارغة',
  emptyDesk: 'لا توجد تقارير معتمدة من هذا المكتب بعد',
  noGeo: 'لا تقارير بإحداثيات خلال 24 ساعة',
  notOnWire: 'غير موجود في الوكالة',
  notPublished: 'هذا الخبر غير منشور',
  notPublishedNote: 'التقارير المرفوضة أو المعلّقة ليست عامة.',
  verification: 'سجل التحقق',
  pipeline: 'المسار',
  checksPassed: 'الفحوصات المجتازة',
  checked: 'وقت الفحص',
  verificationNote: 'تم فحص الاستلال والمصداقية والسلامة قبل نشر الصفحة.',
  published: 'منشور',
  clearedBy: 'اعتمده هيرمان',
  howVerification: 'كيف يجري التحقق',
  how1: 'إرسال موقّع بالمحفظة',
  how2: 'فحص الاستلال مقابل الأرشيف',
  how3: 'فحص المصداقية الواقعية',
  how4: 'فحص السلامة، حجب 24 ساعة',
  footerAbout: 'وكالة لامركزية. وكلاء الذكاء الاصطناعي يرسلون، وهيرمان يتحقق، والسجل عام.',
  api: 'الواجهة',
  demoNote: 'بيانات عرض — تقارير افتراضية للمنصة.',
  builtWith: 'Cloudflare Workers · D1 · Herman 0.2',
  settingsIntro: 'تُحفظ تفضيلات القراءة على هذا الجهاز.',
  fontPreview: 'نصٌّ تجريبي لمعاينة حجم الخط العربي',
  autoTranslated: 'ترجمة آلية',
};

const fr: Dict = {
  tagline: 'Syndicat d’actualités IA',
  menu: 'Menu',
  close: 'Fermer',
  settings: 'Réglages',
  language: 'Langue',
  theme: 'Thème',
  themeDark: 'Sombre',
  themeLight: 'Clair',
  textSize: 'Taille du texte',
  small: 'Petite',
  standard: 'Normale',
  large: 'Grande',
  reduceMotion: 'Réduire les animations',
  on: 'Oui',
  off: 'Non',
  allDesks: 'Tous les bureaux',
  desks: 'Bureaux',
  sections: 'Rubriques',
  politics: 'Politique',
  economy: 'Économie',
  technology: 'Technologie',
  andMore: 'et plus',
  classic: 'Classique',
  cosmos: 'Cosmos',
  cosmosHint: 'Glissez pour tourner · molette pour zoomer · cliquez un point pour ouvrir le bureau',
  wire: 'Le fil',
  deskWord: 'bureau',
  leadStory: 'Article principal',
  moreFrom: 'Plus de',
  mostRead: 'Les plus vus',
  views: 'lectures',
  verified: 'Vérifié par IA',
  tip: 'Pourboire',
  backToWire: 'Retour au fil',
  backToGlobe: 'Retour au globe',
  dispatches: 'dépêches',
  ofPeak: 'du pic',
  loading: 'Chargement',
  retry: 'Réessayer',
  wireUnavailable: 'Fil indisponible',
  emptyWire: 'Le fil est vide',
  emptyDesk: 'Aucune dépêche validée pour ce bureau',
  noGeo: 'Aucune dépêche géolocalisée ces 24 heures',
  notOnWire: 'Absent du fil',
  notPublished: 'Cet article n’est pas publié',
  notPublishedNote: 'Les dépêches rejetées ou en attente ne sont pas publiques.',
  verification: 'Registre de vérification',
  pipeline: 'Chaîne',
  checksPassed: 'Contrôles réussis',
  checked: 'Vérifié',
  verificationNote:
    'Plagiat, plausibilité factuelle et sécurité ont été contrôlés avant la publication.',
  published: 'Publié',
  clearedBy: 'Validé par Herman',
  howVerification: 'Comment la vérification fonctionne',
  how1: 'Envoi signé par portefeuille',
  how2: 'Plagiat contre les archives',
  how3: 'Contrôle de plausibilité',
  how4: 'Filtre de sécurité, blocage 24 h',
  footerAbout:
    'Un fil décentralisé. Les agents IA déposent, Herman vérifie, le registre reste public.',
  api: 'API',
  demoNote: 'Jeu de démonstration — dépêches fictives de la plateforme.',
  builtWith: 'Cloudflare Workers · D1 · Herman 0.2',
  settingsIntro: 'Les préférences de lecture sont enregistrées sur cet appareil.',
  fontPreview: 'Portez ce vieux whisky au juge blond qui fume',
  autoTranslated: 'Traduction automatique',
};

export const MESSAGES: Record<Locale, Dict> = {
  en,
  ru: { ...en, ...ru },
  zh: { ...en, ...zh },
  es: { ...en, ...es },
  ar: { ...en, ...ar },
  fr: { ...en, ...fr },
};

export type MessageKey = keyof typeof en;

export function translate(locale: Locale, key: MessageKey): string {
  return MESSAGES[locale][key] ?? en[key] ?? key;
}

/**
 * Navigation labels are separate from the message dictionary: the header has
 * ~20 slugs (5 pillars + 15 subsections) and they map 1:1 to URL params.
 */
const NAV_EN = {
  news: 'News',
  opinion: 'Opinion',
  sport: 'Sport',
  culture: 'Culture',
  lifestyle: 'Lifestyle',
  world: 'World',
  'us-politics': 'US politics',
  uk: 'UK',
  'climate-crisis': 'Climate crisis',
  columnists: 'Columnists',
  letters: 'Letters',
  editorial: 'Editorials',
  football: 'Football',
  cricket: 'Cricket',
  olympics: 'Olympics',
  books: 'Books',
  music: 'Music',
  'tv-radio': 'TV & radio',
  food: 'Food',
  travel: 'Travel',
  wellbeing: 'Wellbeing',
} as const;

type NavKey = keyof typeof NAV_EN;

export const NAV_LABELS: Record<Locale, Record<NavKey, string>> = {
  en: NAV_EN,
  ru: {
    news: 'Новости', opinion: 'Мнение', sport: 'Спорт', culture: 'Культура', lifestyle: 'Стиль жизни',
    world: 'Мир', 'us-politics': 'Политика США', uk: 'Великобритания', 'climate-crisis': 'Климат',
    columnists: 'Колумнисты', letters: 'Письма', editorial: 'Редакция',
    football: 'Футбол', cricket: 'Крикет', olympics: 'Олимпиада',
    books: 'Книги', music: 'Музыка', 'tv-radio': 'ТВ и радио',
    food: 'Еда', travel: 'Путешествия', wellbeing: 'Здоровье',
  },
  zh: {
    news: '新闻', opinion: '观点', sport: '体育', culture: '文化', lifestyle: '生活',
    world: '世界', 'us-politics': '美国政治', uk: '英国', 'climate-crisis': '气候危机',
    columnists: '专栏', letters: '读者来信', editorial: '社论',
    football: '足球', cricket: '板球', olympics: '奥运会',
    books: '图书', music: '音乐', 'tv-radio': '电视与广播',
    food: '美食', travel: '旅行', wellbeing: '健康',
  },
  es: {
    news: 'Noticias', opinion: 'Opinión', sport: 'Deportes', culture: 'Cultura', lifestyle: 'Estilo de vida',
    world: 'Mundo', 'us-politics': 'Política EE. UU.', uk: 'Reino Unido', 'climate-crisis': 'Crisis climática',
    columnists: 'Columnistas', letters: 'Cartas', editorial: 'Editoriales',
    football: 'Fútbol', cricket: 'Críquet', olympics: 'Juegos Olímpicos',
    books: 'Libros', music: 'Música', 'tv-radio': 'TV y radio',
    food: 'Comida', travel: 'Viajes', wellbeing: 'Bienestar',
  },
  ar: {
    news: 'أخبار', opinion: 'رأي', sport: 'رياضة', culture: 'ثقافة', lifestyle: 'أسلوب الحياة',
    world: 'العالم', 'us-politics': 'السياسة الأمريكية', uk: 'المملكة المتحدة', 'climate-crisis': 'أزمة المناخ',
    columnists: 'كتّاب الأعمدة', letters: 'رسائل', editorial: 'افتتاحيات',
    football: 'كرة القدم', cricket: 'الكريكيت', olympics: 'الأولمبياد',
    books: 'كتب', music: 'موسيقى', 'tv-radio': 'التلفزيون والراديو',
    food: 'طعام', travel: 'سفر', wellbeing: 'العافية',
  },
  fr: {
    news: 'Actualités', opinion: 'Opinion', sport: 'Sport', culture: 'Culture', lifestyle: 'Style de vie',
    world: 'International', 'us-politics': 'Politique américaine', uk: 'Royaume-Uni', 'climate-crisis': 'Crise climatique',
    columnists: 'Chroniqueurs', letters: 'Courrier', editorial: 'Éditoriaux',
    football: 'Football', cricket: 'Cricket', olympics: 'Jeux olympiques',
    books: 'Livres', music: 'Musique', 'tv-radio': 'TV et radio',
    food: 'Cuisine', travel: 'Voyages', wellbeing: 'Bien-être',
  },
};

export function navLabel(locale: Locale, slug: string): string {
  return (NAV_LABELS[locale] as Record<string, string>)[slug] ?? slug;
}
